/* eslint-disable @typescript-eslint/no-explicit-any */
import { DeliveryInformation, NewPurchaseOrder } from 'state/handsell';
import { Duration } from 'js-joda';
import { MAX_BOOKING_GUEST_SIZE } from 'constants/BookingConstants';
import { OptionData } from 'selectors/handsellSelectors';
import { RefundTypes } from 'constants/RefundTypes';
import { cloneDeep, isEqual, noop } from 'es-toolkit';
import { core } from '@tock/coretypes';
import { createAction } from 'redux-actions';
import { dashboard } from 'types';
import { getDiner } from '@tock/shared/utils/dinerUtils';
import { handlePasscodeErrorsInModal } from 'utils/passcodeUtils';
import AppConstants from 'constants/AppConstants';
import HandSellType from 'constants/HandSellType';
import protos from 'utils/protos';

const { PurchaseOrder, Patron, PatronNote, DietaryNote, LockTicketsRequest, UnlockRequest } =
  protos;

function processPurchaseOrder(purchaseOrderInput: core.IPurchaseOrder): core.IPurchaseOrder {
  const purchaseOrder = cloneDeep(purchaseOrderInput);
  if (isEqual(purchaseOrder.patron, getDiner(purchaseOrder))) {
    delete purchaseOrder.dinerPatron;
  }
  return purchaseOrder;
}

export function lockTickets(options: {
  ticketGroupId?: number;
  ticketTypeId: number;
  size: number;
  dateTime: string;
  tableId?: number[];
}): ApiAction<core.TicketGroup> {
  const { ticketGroupId, ticketTypeId, size, dateTime, tableId } = options;
  return {
    TockApi: {
      putProto: '/ticket/group/lock',
      type: AppConstants.HAND_SELL_LOCK,
      request: LockTicketsRequest.fromObject({
        ticketGroupId,
        ticketTypeId,
        size,
        dateTime,
        tableId: tableId || [],
      }),
    },
  };
}

export function unlockTickets(options: {
  ticketSubsetId: number;
  ticketGroupId: number;
}): ApiAction<core.TicketGroup> {
  const { ticketSubsetId, ticketGroupId } = options;
  return {
    TockApi: {
      putProto: '/ticket/unlock',
      type: AppConstants.HAND_SELL_UNLOCK,
      request: UnlockRequest.fromObject({
        ticketSubsetId,
        ticketGroupId,
      }),
    },
  };
}

export function bookTickets(options: {
  ticketGroupId: number;
  numTickets: number;
  patron?: core.Patron;
  handSellType?: string;
  ticketSubsetId?: number;
  dateTime?: string;
  tableIdsToBook?: number[];
  // Optional override to book a ticket type differing from that of the ticket group
  ticketTypeId?: number;
  seatingAreaId?: number;
}): ApiAction {
  const {
    ticketGroupId,
    numTickets,
    patron,
    handSellType,
    ticketSubsetId,
    ticketTypeId,
    dateTime,
    tableIdsToBook,
    seatingAreaId,
  } = options;

  const numTicketsWithMaxLimit = Math.min(numTickets, MAX_BOOKING_GUEST_SIZE);

  return {
    TockApi: {
      getProto: `/ticket/group/${ticketGroupId}?slim=true`,
      type: AppConstants.HAND_SELL_OPEN,
      context: {
        handSellType,
        numTickets: numTicketsWithMaxLimit,
        patron,
        ticketSubsetId,
        ticketTypeId,
        dateTime,
        tableIdsToBook,
        seatingAreaId,
      },
    },
  };
}

export function editBooking(
  purchase: core.PastPurchase,
  ticketGroup?: core.TicketGroup,
  ui?: any,
  allTicketTypes?: core.TicketType[]
): ApiAction {
  return {
    TockApi: {
      getProto: `/purchase/order/generate?purchaseId=${purchase.id}`,
      type: AppConstants.HAND_SELL_EDIT,
      context: {
        ticketGroup,
        purchase,
        ui,
        allTicketTypes,
      },
    },
  };
}

export function close(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CLEAR_ALL,
  };
}

export function updateTurnTimeMin(turnTimeMin: number): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_TURN_TIME,
    payload: turnTimeMin,
  };
}

export function updateGratuity(
  selectedGratuityRate: number,
  gratuityCents: number
): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_GRATUITY,
    payload: {
      selectedGratuityRate,
      gratuityCents,
    },
  };
}

export function updateAvailableOptions(
  ticketGroup: core.TicketGroup,
  ticketTypeTicketOptionGroups: core.TicketOptionGroup[],
  ticketTypeTicketOptions: core.TicketOption[],
  ticketTypes?: core.TicketType[]
): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_AVAILABLE_OPTIONS,
    payload: {
      ticketGroup,
      ticketTypeTicketOptionGroups,
      ticketTypeTicketOptions,
      ticketTypes,
    },
  };
}

export function updateDateTime(newDateTime: string): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_DATE_TIME,
    payload: newDateTime,
  };
}

export function updateTime(newLocalTime: string): BaseTockAction {
  return {
    type: 'HAND_SELL_UPDATE_TIME',
    payload: newLocalTime,
  };
}

export function updateDate(newLocalDate: string): BaseTockAction {
  return {
    type: 'HAND_SELL_UPDATE_DATE',
    payload: newLocalDate,
  };
}

export function updateExcludeFromPacing(excludeFromPacing: boolean): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_EXCLUDE_FROM_PACING,
    payload: excludeFromPacing,
  };
}

export function updateOptions(options: core.IOptionOrder[]): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_OPTIONS,
    payload: options,
  };
}

export function updateHandSellType(handSellType: HandSellType): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_TYPE,
    payload: handSellType,
  };
}

export function updateNumTickets(numTickets: number): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_NUM_TICKETS,
    payload: numTickets,
  };
}

export function updateSeatingArea(seatingAreaId: number): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_SEATING_AREA,
    payload: { seatingAreaId },
  };
}

export function updateExperience(
  ticketTypeId: number,
  ticketTypeName: string,
  ticketTypePrice: number,
  fee: core.IFeePrice[],
  optionData: OptionData,
  extraChargeDetails: core.ExtraChargeDetails
): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_EXPERIENCE,
    payload: {
      ticketTypeId,
      ticketTypeName,
      ticketTypePrice,
      fee,
      optionData,
      extraChargeDetails,
    },
  };
}

export function updateCompAmount(compAmountCents: number): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_COMP_AMOUNT,
    payload: compAmountCents,
  };
}

export function updateAdminDiscountAmount(discountAmountCents: number): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_POST_TOTAL_ADMIN_COMP_AMOUNT,
    payload: discountAmountCents,
  };
}

/*
 * Dispatches a message informing listeners of an update to the question answers selected in the hand sell box.
 * @param {Object} responses object mapping question IDs (as numbers) to bool responses
 */
export function updateQuestionResponses(responses: any): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_CHECKED_QUESTIONS,
    payload: responses,
  };
}

/*
 * Dispatches a message informing listeners of an update to the freeform question answers in the hand sell box.
 * @param {Object} responses object mapping question IDs (as numbers) to string responses
 */
export function updateFreeformQuestionResponses(responses: any): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_FREEFORM_QUESTIONS,
    payload: responses,
  };
}

export function fetchActivePromoCodes(ticketTypeId: number): ApiAction {
  return {
    TockApi: {
      getListProto: `/ticket/type/${ticketTypeId}/promo/code`,
      type: AppConstants.HAND_SELL_PROMOCODE_LIST,
    },
  };
}

export function fetchEligiblePromoCodes(ticketTypeId: number, patron: core.IPatron): ApiAction {
  return {
    TockApi: {
      postListProto: `/ticket/type/${ticketTypeId}/promo/code/eligible`,
      type: AppConstants.HAND_SELL_PROMOCODE_ELIGIBLITY,
      request: Patron.fromObject(patron),
    },
  };
}

export function updatePatron(patron: core.IPatron, diner: core.IPatron): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_PATRON,
    payload: {
      patron,
      diner,
    },
  };
}

export function updatePaymentCardName(name: string): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_PAYMENT_CARD_NAME,
    payload: {
      name,
    },
  };
}

export function updatePaymentCard(paymentCard: core.IPaymentCard): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_PAYMENT_CARD,
    payload: {
      paymentCard,
    },
  };
}

export function clearPaymentCard(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CLEAR_PAYMENT_CARD,
  };
}

export function updateRefundType(refundOnDowngrade: RefundTypes): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_REFUND_TYPE,
    payload: refundOnDowngrade,
  };
}

export function clearRefundType(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CLEAR_REFUND_TYPE,
  };
}

export function updateLockDuration(lockedForDuration: Duration): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_LOCK_DURATION,
    payload: lockedForDuration,
  };
}

export function clearLockDuration(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CLEAR_LOCK_DURATION,
  };
}

export function updateExpirationAvailability(makeAvailable: boolean): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_AVAILABILITY,
    payload: makeAvailable,
  };
}

export function addCustomCharge(customChargeOrder: core.ICustomChargeOrder): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CUSTOM_CHARGE_ADD,
    payload: customChargeOrder,
  };
}

export function removeCustomCharge(customChargeOrder: core.ICustomChargeOrder): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CUSTOM_CHARGE_REMOVE,
    payload: customChargeOrder,
  };
}

export function updateRescheduleOptions(rescheduleOptions: core.RescheduleOptions): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_RESCHEDULE_OPTIONS,
    payload: rescheduleOptions,
  };
}

export function calculatePrice(purchaseOrder: NewPurchaseOrder): ApiAction {
  return {
    TockApi: {
      postProto: '/ticket/price',
      type: AppConstants.HAND_SELL_CALCULATE_PRICE,
      request: PurchaseOrder.fromObject({
        ...purchaseOrder,
        adminOverride: true,
      }),
    },
  };
}

export function purchaseReservation(
  purchaseOrderInput: core.IPurchaseOrder,
  options?: {
    passcode?: string;
    successMessageType?: 'TOAST' | 'HIDDEN';
  }
): ApiAction {
  const purchaseOrder = processPurchaseOrder(purchaseOrderInput);
  const successMessageType = options?.successMessageType || 'TOAST';
  const toast =
    successMessageType === 'TOAST'
      ? purchaseOrder.basePurchaseId
        ? 'Reservation edited'
        : 'Reservation created'
      : undefined;
  return {
    TockApi: {
      postProto: '/admin/purchase',
      type: AppConstants.HAND_SELL_PURCHASE,
      request: PurchaseOrder.fromObject(purchaseOrder),
      toast,
      onError: {
        3005: noop, // Ignore errors for TICKET_GROUP_TOO_SMALL
        ...handlePasscodeErrorsInModal,
        defaultToOriginal: true,
      },
      requestScope: {
        passcode: options ? options.passcode : undefined,
      },
    },
  };
}

export function purchaseViaEmail(
  purchaseOrderInput: core.IPurchaseOrder,
  makeAvailable: boolean,
  passcode?: string
): ApiAction {
  const purchaseOrder = processPurchaseOrder(purchaseOrderInput);
  return {
    TockApi: {
      postProto: `/admin/purchase/send-verify-email?makeAvailableOnUnlock=${makeAvailable}`,
      type: AppConstants.HAND_SELL_PURCHASE_EMAIL,
      request: PurchaseOrder.fromObject(purchaseOrder),
      toast: `Book by email sent to ${purchaseOrder?.patron?.email}`,
      onError: {
        ...handlePasscodeErrorsInModal,
        3041: noop, // don't show redundant toast for overbooking confirmation modal
        defaultToOriginal: true,
      },
      requestScope: {
        passcode,
      },
    },
  };
}

export function purchaseViaSms(
  purchaseOrderInput: core.IPurchaseOrder,
  makeAvailable: boolean,
  passcode?: string
): ApiAction {
  const purchaseOrder = processPurchaseOrder(purchaseOrderInput);
  return {
    TockApi: {
      postProto: `/admin/purchase/send-verify-sms?makeAvailableOnUnlock=${makeAvailable}`,
      type: AppConstants.HAND_SELL_PURCHASE_SMS,
      request: PurchaseOrder.fromObject(purchaseOrder),
      toast: `Book by text sent to ${purchaseOrder?.patron?.phone}`,
      requestScope: {
        passcode,
      },
    },
  };
}

export function applyGiftCard(
  giftCardNumber: string,
  giftCardPin: string,
  amountCents: number,
  business: core.Business
): ApiAction {
  return {
    TockApi: {
      getProto: `/giftcard/${business.giftCardProvider.toLowerCase()}/${giftCardNumber}/balance?pin=${giftCardPin}`,
      type: AppConstants.HAND_SELL_GIFT_CARD_APPLY,
      context: {
        giftCardNumber,
        giftCardPin,
        amountCents,
        business,
      },
      onError: {
        1001: 'The gift card number supplied was invalid or not found',
        defaultToOriginal: true,
      },
    },
  };
}

export function getGiftCardBalance(giftCardNumber: string): ApiAction {
  return {
    TockApi: {
      getProto: `/giftcard/tcc/${giftCardNumber}/balance`,
      type: AppConstants.NULL,
      onError: {
        1001: 'The gift card number supplied was invalid or not found',
        defaultToOriginal: true,
      },
    },
  };
}
export function setGiftCard(
  giftCardNumber: string,
  giftCardPin: string,
  amountCents: number,
  business: any
): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_GIFT_CARD_APPLY_DONE,
    payload: amountCents,
    context: {
      giftCardNumber,
      giftCardPin,
      amountCents,
      business,
    },
  };
}

export function removeGiftCard(giftCardNumber: any): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_GIFT_CARD_REMOVE,
    context: { giftCardNumber },
  };
}

export function setPromoCode(discount?: core.IDiscount): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_PROMOCODE,
    payload: discount,
  };
}

export function clearPromoCode(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_PROMOCODE,
    payload: undefined,
  };
}

export function updateEmailPolicy(newPolicy: core.EmailPolicy): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_EMAIL_POLICY,
    payload: newPolicy,
  };
}

export function updateBookingNote(newNote: string): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_BOOKING_NOTE,
    payload: newNote,
  };
}

export function updateDietaryNote(newNote: string): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_DIETARY_NOTE,
    payload: newNote,
  };
}

/**
 * updates the local notes state to be used at the end of the handsell process
 *
 * @param { note, dietaryNote }
 */
export function updateHandSellGuestNotes({
  note,
  dietaryNote,
}: {
  note?: string;
  dietaryNote?: string;
}): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_GUEST_NOTES,
    payload: {
      note,
      dietaryNote,
    },
  };
}

/**
 * clears the local notes state
 */
export function clearGuestNotes(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CLEAR_GUEST_NOTES,
  };
}

export function clearCompletedPurchase(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CLEAR_COMPLETED_PURCHASE,
  };
}

export function clearCompletedPurchaseEmail(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CLEAR_COMPLETED_PURCHASE_EMAIL,
  };
}

/**
 * updates visit tags
 *
 * @param visitTags
 */
export function updateHandSellVisitTags(visitTags: string[]): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_VISIT_TAGS,
    payload: {
      visitTags,
    },
  };
}

/**
 * updates guest tags
 *
 * @param guestTags
 */
export const updateHandSellGuestTags = createAction(
  AppConstants.HAND_SELL_UPDATE_GUEST_TAGS,
  (guestTags: string[]) => ({ guestTags })
);

export function clearGuestTags(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CLEAR_GUEST_TAGS,
  };
}

export function fetchGuestProfile(patronId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/guest/profile/${patronId}`,
      type: AppConstants.HAND_SELL_FETCH_GUEST_PROFILE,
    },
  };
}

/**
 * Get existing profile for the given email
 *
 * @param {String} email Patron's email
 * @returns {Object} A Redux TockApi action.
 */
export function lookupGuestProfile(email: string): ApiAction {
  return {
    TockApi: {
      getProto: `/guest/profile/lookup?email=${encodeURIComponent(email)}`,
      type: AppConstants.HAND_SELL_FETCH_GUEST_PROFILE,
      onError: {
        1001: '', // Not found
        defaultToOriginal: true,
      },
    },
  };
}

export function clearGuestProfile(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CLEAR_GUEST_PROFILE,
  };
}

export function setGuestProfileNote(
  patronId: number,
  businessId: number,
  note: Partial<dashboard.DietaryNote> | Partial<dashboard.PatronNote>,
  type: dashboard.NoteType,
  toast?: string
): ApiAction {
  let request;
  if (type === 'DIETARY') {
    request = DietaryNote.fromObject(note);
  } else {
    request = PatronNote.fromObject(note);
  }

  const pathSuffix = type === 'DIETARY' ? '/dietary/note' : '/note';

  return {
    TockApi: {
      postProto: `/patron${pathSuffix}`,
      type: AppConstants.HAND_SELL_UPDATE_GUEST_PROFILE_NOTE,
      request,
      context: { businessId, type },
      toast,
    },
  };
}

export function setPurchaseOrderKeyValue(keyValueObject: Dictionary<string>): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_SET_KEY_VALUES,
    payload: keyValueObject,
  };
}

export function setPurchaseOrderDeliveryInformation(
  deliveryInformation: Partial<DeliveryInformation>
): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_UPDATE_DELIVERY_INFORMATION,
    payload: deliveryInformation,
  };
}

export function clearPurchaseOrderKeyValue(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_CLEAR_KEY_VALUES,
  };
}

export function setIncreaseSizeFlag(): BaseTockAction {
  return {
    type: AppConstants.HAND_SELL_INCREASE_SIZE_FLAG,
  };
}
